import { useCallback, useEffect } from "react";
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
} from "reactflow";
import { toast } from "@/components/ui/notify";
import { v4 as uuid } from "uuid";
import type { CanvasNodeData } from "@/types/canvas.types.js";
import { labelOf } from "../../canvas-transforms";

export interface UseCanvasFlowEventsParams {
  isEdit: boolean;
  draftNodes: Node<CanvasNodeData>[];
  selectedNodeId: string | null;
  setDraftNodes: React.Dispatch<React.SetStateAction<Node<CanvasNodeData>[]>>;
  setDraftEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  announce: (msg: string) => void;
  handleNodeDelete: () => void;
}

export interface CanvasFlowEvents {
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (params: Connection) => void;
  onEdgesDelete: (deleted: Edge[]) => void;
  onDeleteEdgeById: (edgeId: string) => void;
}

export function useCanvasFlowEvents(params: UseCanvasFlowEventsParams): CanvasFlowEvents {
  const {
    isEdit,
    draftNodes,
    selectedNodeId,
    setDraftNodes,
    setDraftEdges,
    setIsDirty,
    setSelectedNodeId,
    announce,
    handleNodeDelete,
  } = params;

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (!isEdit) return;
      setDraftNodes((prev) => applyNodeChanges(changes, prev));
      const positional = changes.some(
        (c) => c.type === "position" || c.type === "remove" || c.type === "add"
      );
      if (positional) setIsDirty(true);
    },
    [isEdit, setDraftNodes, setIsDirty]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (!isEdit) return;
      setDraftEdges((prev) => applyEdgeChanges(changes, prev));
      if (changes.some((c) => c.type !== "select")) setIsDirty(true);
    },
    [isEdit, setDraftEdges, setIsDirty]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!isEdit || !connection.source || !connection.target) return;
      const src = draftNodes.find((n) => n.id === connection.source);
      const tgt = draftNodes.find((n) => n.id === connection.target);
      if (!src || !tgt) return;
      if (src.type === "END") {
        toast.error("Nó END não pode ser source de conexão.");
        return;
      }
      if (tgt.type === "START") {
        toast.error("Nó START não pode ser target de conexão.");
        return;
      }
      setDraftEdges((prev) =>
        addEdge(
          {
            id: uuid(),
            source: connection.source!,
            target: connection.target!,
            type: "PRODUCTION_FLOW",
            data: {},
          },
          prev
        )
      );
      setIsDirty(true);
      announce(`Conexão criada de ${labelOf(src)} para ${labelOf(tgt)}.`);
    },
    [isEdit, draftNodes, setDraftEdges, setIsDirty, announce]
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (!isEdit || deleted.length === 0) return;
      announce(
        deleted.length === 1 ? "Conexão removida." : `${deleted.length} conexões removidas.`
      );
    },
    [isEdit, announce]
  );

  const onDeleteEdgeById = useCallback(
    (edgeId: string) => {
      if (!isEdit) return;
      setDraftEdges((prev) => prev.filter((e) => e.id !== edgeId));
      setIsDirty(true);
      announce("Conexão removida.");
    },
    [isEdit, setDraftEdges, setIsDirty, announce]
  );

  useEffect(() => {
    if (!isEdit) return;
    function onKey(e: KeyboardEvent) {
      if (!selectedNodeId) {
        if (e.key === "Escape") (document.activeElement as HTMLElement)?.blur();
        return;
      }
      const tag = (document.activeElement?.tagName ?? "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      const step = e.shiftKey ? 50 : 10;
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        handleNodeDelete();
        return;
      } else if (e.key === "Escape") {
        setSelectedNodeId(null);
        announce("Seleção limpa.");
        return;
      } else return;

      e.preventDefault();
      setDraftNodes((prev) =>
        prev.map((n) =>
          n.id === selectedNodeId
            ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
            : n
        )
      );
      setIsDirty(true);
      const moved = draftNodes.find((n) => n.id === selectedNodeId);
      if (moved) {
        announce(
          `Nó ${moved.data.label ?? moved.id} movido para (${Math.round(
            moved.position.x + dx
          )}, ${Math.round(moved.position.y + dy)}).`
        );
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, selectedNodeId, draftNodes]);

  return { onNodesChange, onEdgesChange, onConnect, onEdgesDelete, onDeleteEdgeById };
}
